import { storableError } from '../../util/errors';
import { filterCompletedListings } from '../../util/listingsHelpers';

// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/BrowseListingsPage/SET_INITIAL_STATE';

export const LOAD_LISTINGS_REQUEST = 'app/BrowseListingsPage/LOAD_LISTINGS_REQUEST';
export const LOAD_LISTINGS_SUCCESS = 'app/BrowseListingsPage/LOAD_LISTINGS_SUCCESS';
export const LOAD_LISTINGS_ERROR = 'app/BrowseListingsPage/LOAD_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  listings: [],
  pagination: null,
  listingsInProgress: false,
  listingsError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_STATE:
      return { ...initialState };
    case LOAD_LISTINGS_REQUEST:
      return {
        ...state,
        listingsInProgress: true,
        listingsError: null,
      };
    case LOAD_LISTINGS_SUCCESS:
      return {
        ...state,
        listingsInProgress: false,
        listings: payload.listings,
        pagination: payload.pagination,
      };
    case LOAD_LISTINGS_ERROR:
      return {
        ...state,
        listingsInProgress: false,
        listingsError: payload,
      };
    default:
      return state;
  }
}

// ================ Selectors ================ //

export const selectBrowseListingsPage = state => state.BrowseListingsPage;

export const selectListings = state => {
  const { listings } = selectBrowseListingsPage(state);
  return listings;
};

export const selectPagination = state => {
  const { pagination } = selectBrowseListingsPage(state);
  return pagination;
};

export const selectListingsInProgress = state => {
  const { listingsInProgress } = selectBrowseListingsPage(state);
  return listingsInProgress;
};

export const selectListingsError = state => {
  const { listingsError } = selectBrowseListingsPage(state);
  return listingsError;
};

// ================ Action creators ================ //

export const setInitialState = () => ({
  type: SET_INITIAL_STATE,
});

export const loadListingsRequest = () => ({
  type: LOAD_LISTINGS_REQUEST,
});

export const loadListingsSuccess = (listings, pagination) => ({
  type: LOAD_LISTINGS_SUCCESS,
  payload: { listings, pagination },
});

export const loadListingsError = error => ({
  type: LOAD_LISTINGS_ERROR,
  payload: error,
});

// ================ Thunks ================ //

export const loadListings = (page = 1) => async (dispatch, getState, sdk) => {
  dispatch(loadListingsRequest());

  const perPage = 24; // Show 24 listings per page

  const queryParams = {
    page,
    perPage,
    sort: "pub_rank, createdAt",
    include: ['author', 'author.profileImage', 'images'],
    pub_isFromManufacturer: false,
    pub_isManufacturer: false,
    pub_hidden: false,
    pub_batchUpload: false,
    pub_isTestListing: false,
    minStock: 0,
    stockMode: 'match-undefined',
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
      'publicData.isAssociated',
      'publicData.creatorId',
      'publicData.creatorType',
      'publicData.deliveryPreference',
      'publicData.shippingAddress',
      'publicData.location',
      'publicData.storedAddress',
      'publicData.category',
      'publicData.subcategory',
      'publicData.videos',
      'publicData.isManufacturer',
      'publicData.hidden',
      'publicData.batchUpload',
      'publicData.isTestListing',
      'publicData.importData',
      'publicData.importSource',
      'publicData.condition',
      'publicData.tags',
      'publicData.isFromManufacturer',
      'publicData.manufacturerListingId',
      'publicData.isFeatured',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.profileImage', 'profile.publicData'],
    'fields.image': [
      'variants.landscape-crop',
      'variants.listing-card',
      'variants.square-small'
    ],
    'limit.images': 1,
  };

  try {
    const response = await sdk.listings.query(queryParams);

    if (response.data && response.data.data) {
      // Filter out draft/incomplete listings
      let filteredListings = filterCompletedListings(response.data.data || []);

      // Filter out manufacturer listings (already filtered by API, but double-check)
      filteredListings = filteredListings.filter(l => !l?.attributes?.publicData?.isManufacturer);

      // Process the listings to include author and images from the included data
      const included = response.data.included || [];

      // Filter out listings from unapproved sellers
      const includedUsers = included.filter(item => item.type === 'user');
      filteredListings = filteredListings.filter(listing => {
        const authorId = listing.relationships?.author?.data?.id?.uuid;
        const author = includedUsers.find(user => user.id.uuid === authorId);
        const isApprovedSeller = author?.attributes?.profile?.publicData?.approved_seller === true;
        return isApprovedSeller;
      });
      const processedListings = filteredListings.map(listing => {
        // Find author from included data
        const authorId = listing.relationships?.author?.data?.id?.uuid;
        const author = included.find(item =>
          item.type === 'user' && item.id.uuid === authorId
        );

        // Find author's profile image from included data
        let authorWithProfileImage = author;
        if (author && author.relationships?.profileImage?.data?.id?.uuid) {
          const profileImageId = author.relationships.profileImage.data.id.uuid;
          const profileImage = included.find(item =>
            item.type === 'image' && item.id.uuid === profileImageId
          );
          if (profileImage) {
            authorWithProfileImage = {
              ...author,
              profileImage: profileImage
            };
          }
        }

        // Find images from included data
        const imageIds = listing.relationships?.images?.data?.map(img => img.id.uuid) || [];
        const images = included.filter(item =>
          item.type === 'image' && imageIds.includes(item.id.uuid)
        );

        return {
          ...listing,
          author: authorWithProfileImage,
          images: images
        };
      });

      // Fetch manufacturer images for listings that need them
      const listingsNeedingManufacturerImages = processedListings.filter(listing => {
        const publicData = listing?.attributes?.publicData || {};
        const hasNoImages = !listing.images || listing.images.length === 0;
        return hasNoImages && publicData.isFromManufacturer && publicData.manufacturerListingId;
      });

      // Fetch all manufacturer listings in parallel
      const manufacturerImagePromises = listingsNeedingManufacturerImages.map(listing => {
        const manufacturerListingId = listing.attributes.publicData.manufacturerListingId;
        return sdk.listings.show({
          id: manufacturerListingId,
          include: ['images'],
          'fields.image': [
            'variants.landscape-crop',
            'variants.listing-card',
            'variants.square-small'
          ],
          'limit.images': 1,
        }).catch(err => {
          console.error('Error fetching manufacturer images for listing:', manufacturerListingId, err);
          return null;
        });
      });

      // Wait for all manufacturer images to be fetched
      const manufacturerResponses = await Promise.all(manufacturerImagePromises);

      // Map manufacturer images to dealer listings
      const finalListings = processedListings.map(listing => {
        const publicData = listing?.attributes?.publicData || {};
        const hasNoImages = !listing.images || listing.images.length === 0;

        if (hasNoImages && publicData.isFromManufacturer && publicData.manufacturerListingId) {
          // Find the manufacturer response for this listing
          const manufacturerListingId = publicData.manufacturerListingId;
          const manufacturerResponse = manufacturerResponses.find(res =>
            res?.data?.data?.id?.uuid === manufacturerListingId
          );

          if (manufacturerResponse?.data?.included) {
            // Get images from manufacturer listing
            const manufacturerImages = manufacturerResponse.data.included.filter(item =>
              item.type === 'image'
            );

            if (manufacturerImages.length > 0) {
              return {
                ...listing,
                images: manufacturerImages
              };
            }
          }
        }

        return listing;
      });

      // Extract pagination info
      const pagination = response.data.meta || null;

      dispatch(loadListingsSuccess(finalListings, pagination));
      return { listings: finalListings, pagination };
    }

    dispatch(loadListingsSuccess([], null));
    return { listings: [], pagination: null };
  } catch (error) {
    const err = storableError(error);
    dispatch(loadListingsError(err));
    throw err;
  }
};

// ================ Export ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const page = parseInt(search?.page) || 1;
  return dispatch(loadListings(page));
};
